import React, { useEffect, useState } from 'react'
import { Box, Button, ButtonBase, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { AddRounded, AssignmentOutlined, CloseRounded, DescriptionOutlined, Inventory2Outlined, SearchRounded } from '@mui/icons-material'
import { getSensitiveCandidates } from '../visionApi'
import { SINGLE_ITEM_TYPES, cascadeText } from './cameraControlRules'

// C15.3: Sun is asked 300 ms after the last key, and only from 2 characters on.
const SEARCH_DELAY_MS = 300
const MIN_SEARCH_LENGTH = 2

// Design (Main.dc.html): the icon of each type, in the results and in the list.
const ICON_OF = {
  PART_REFERENCE: Inventory2Outlined,
  ORDER: AssignmentOutlined,
  DOCUMENT: DescriptionOutlined
}
const TypeIcon = ({ type, color }) => {
  const Icon = ICON_OF[type] || DescriptionOutlined
  return <Icon fontSize='small' sx={{ color, flex: '0 0 auto' }} />
}

const NO_ANSWER = { query: '', results: [] }

/**
 * Story 213, reopening 3 (K2, K4, C15.3; design Main.dc.html, «Single items») — choosing what is protected by name: a
 * type, a search of Sun's candidates and "Protect"; below, the one list "Protected · N" of the three types with their
 * cascade and "Remove". Protect and Remove are pending changes of `items` (useSensitiveItems), saved by the page's bar.
 * `disabled`: the scope is All of MIC (dimmed with the step, keeping what is chosen).
 */
const SingleItems = ({ items, disabled, opacity, t_ }) => {
  const [type, setType] = useState(SINGLE_ITEM_TYPES[0].type)
  const [text, setText] = useState('')
  const [answer, setAnswer] = useState(NO_ANSWER)
  const query = text.trim()
  const searched = query.length >= MIN_SEARCH_LENGTH
  const answered = searched && answer.query === type + ' ' + query

  useEffect(() => {
    if (!searched) {
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      getSensitiveCandidates(type, query)
        .then((results) => {
          if (!cancelled) {
            setAnswer({ query: type + ' ' + query, results: Array.isArray(results) ? results : [] })
          }
        })
        .catch(() => {})
    }, SEARCH_DELAY_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [type, query, searched])

  const results = answered ? answer.results : []
  const hint = query.length > 0 && !searched ? 'Type at least 2 characters' : answered && results.length === 0 ? 'No results' : ''

  return (
    <Stack component='section' aria-label={t_('Single items')} spacing={1.5} sx={{ opacity }}>
      <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>{t_('Single items')}</Typography>
      <Stack direction='row' flexWrap='wrap' alignItems='center' gap={1.25}>
        <Box role='radiogroup' sx={{ display: 'flex', border: '1px solid #555555', borderRadius: 2, overflow: 'hidden' }}>
          {SINGLE_ITEM_TYPES.map((option, index) => (
            <ButtonBase
              key={option.type}
              role='radio'
              aria-checked={type === option.type}
              disabled={disabled}
              onClick={() => setType(option.type)}
              sx={{
                minHeight: 38,
                px: 1.75,
                borderLeft: index > 0 ? '1px solid #555555' : 'none',
                bgcolor: type === option.type ? '#2B3A31' : '#383838',
                color: type === option.type ? '#7FE1A7' : '#E6E6E6',
                fontSize: 14,
                fontWeight: type === option.type ? 700 : 500
              }}
            >
              {t_(option.text)}
            </ButtonBase>
          ))}
        </Box>
        <TextField
          type='search'
          size='small'
          value={text}
          disabled={disabled}
          placeholder={t_('Search')}
          onChange={(event) => setText(event.target.value)}
          inputProps={{ 'aria-label': t_('Search') }}
          InputProps={{ startAdornment: <InputAdornment position='start'><SearchRounded fontSize='small' /></InputAdornment> }}
          sx={{ flex: '1 1 240px' }}
        />
      </Stack>
      {hint !== '' && <Typography variant='body2' sx={{ color: '#A8A8A8' }}>{t_(hint)}</Typography>}
      {results.length > 0 && (
        <Box sx={{ borderRadius: '10px', bgcolor: '#383838', overflow: 'hidden' }}>
          {results.map((candidate) => {
            const taken = items.isProtected(candidate)
            return (
              <Stack key={candidate.key} data-testid='item-result' direction='row' alignItems='center' spacing={1.5} sx={{ px: 1.75, py: 1.25, borderTop: '1px solid #444444' }}>
                <TypeIcon type={candidate.type} color='#A8A8A8' />
                <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{candidate.name}</Typography>
                <Typography variant='body2' sx={{ color: '#CFCFCF', flex: '1 1 auto', minWidth: 0 }}>{candidate.detail}</Typography>
                <Button
                  variant='outlined'
                  size='small'
                  disabled={disabled || taken}
                  onClick={() => items.protect(candidate)}
                  {...(taken ? {} : { startIcon: <AddRounded /> })}
                  sx={{ borderRadius: 999, minHeight: 34, flex: '0 0 auto' }}
                >
                  {taken ? t_('Protected') : t_('Protect')}
                </Button>
              </Stack>
            )
          })}
        </Box>
      )}

      <Typography data-testid='protected-items-header' variant='subtitle2' sx={{ color: 'text.secondary', pt: 0.5 }}>
        {t_('Protected · {{count}}', { count: items.list.length })}
      </Typography>
      {items.list.length === 0
        ? <Typography variant='body2' sx={{ color: '#A8A8A8' }}>{t_('No protected items yet')}</Typography>
        : (
          <Stack component='ul' spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {items.list.map((mark) => {
              const cascade = cascadeText(mark.cascade || {}, t_)
              return (
                <Stack
                  component='li'
                  key={mark.type + ' ' + mark.key}
                  data-testid='protected-item'
                  data-type={mark.type}
                  direction='row'
                  flexWrap='wrap'
                  alignItems='center'
                  gap={1.5}
                  sx={{ px: 1.75, py: 1.25, borderRadius: '10px', bgcolor: '#2B2B2B', border: '1px solid #FF8F00' }}
                >
                  <TypeIcon type={mark.type} color='#FF8F00' />
                  {/* H-C15-7 (c): the element no longer exists; its key in grey, so the mark can be removed. */}
                  {mark.name
                    ? <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{mark.name}</Typography>
                    : <Typography sx={{ color: '#8A8A8A' }}>{mark.key}</Typography>}
                  {cascade !== '' && <Typography data-testid='item-cascade' sx={{ fontSize: 13, color: '#FFCC80' }}>{cascade}</Typography>}
                  <Button
                    variant='outlined'
                    size='small'
                    disabled={disabled}
                    onClick={() => items.remove(mark)}
                    endIcon={<CloseRounded />}
                    sx={{ ml: 'auto', borderRadius: 999, minHeight: 36, color: '#E6E6E6', borderColor: '#6A6A6A' }}
                  >
                    {t_('Remove')}
                  </Button>
                </Stack>
              )
            })}
          </Stack>
          )}
    </Stack>
  )
}

export default SingleItems
